// Knowledge base API: house methodology + per-tenant documents, with lifecycle
// controls so only reviewed, current material can reach the model.
import { supabase } from '@/lib/supabase';

export type KnowledgeScope = 'house' | 'org';
export type KnowledgeLifecycle = 'draft' | 'approved' | 'archived';

export type KnowledgeDocument = {
  id: string;
  scope: KnowledgeScope;
  org_id: string | null;
  title: string;
  source_kind: 'text' | 'file' | 'url';
  tags: string[];
  status: 'pending' | 'ready' | 'failed';
  lifecycle_status: KnowledgeLifecycle;
  version: number;
  quality_score: number;
  review_at: string | null;
  superseded_by: string | null;
  ingest_warnings: string[];
  error_message: string | null;
  chunk_count: number;
  created_at: string;
  updated_at: string;
};

const COLS =
  'id, scope, org_id, title, source_kind, tags, status, lifecycle_status, version, quality_score, review_at, superseded_by, ingest_warnings, error_message, chunk_count, created_at, updated_at';

export async function listKnowledgeDocuments(
  scope: KnowledgeScope,
  orgId?: string,
): Promise<KnowledgeDocument[]> {
  let q = supabase.from('knowledge_documents').select(COLS).eq('scope', scope);
  if (scope === 'org' && orgId) q = q.eq('org_id', orgId);
  const { data, error } = await q.order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((d) => ({
    ...(d as unknown as KnowledgeDocument),
    ingest_warnings: Array.isArray((d as { ingest_warnings?: unknown }).ingest_warnings)
      ? ((d as { ingest_warnings: string[] }).ingest_warnings)
      : [],
  }));
}

export async function ingestKnowledgeDocument(args: {
  scope: KnowledgeScope;
  orgId?: string | null;
  title: string;
  content: string;
  tags?: string[];
  documentId?: string;
}): Promise<{ document_id: string; chunks: number; version?: number; warnings?: string[] }> {
  const { data, error } = await supabase.functions.invoke('knowledge-ingest', {
    body: {
      scope: args.scope,
      org_id: args.orgId ?? null,
      title: args.title,
      content: args.content,
      tags: args.tags ?? [],
      document_id: args.documentId ?? null,
    },
  });
  if (error) throw error;
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
  return data as { document_id: string; chunks: number; version?: number; warnings?: string[] };
}

export async function setKnowledgeLifecycle(
  id: string,
  lifecycle: KnowledgeLifecycle,
): Promise<void> {
  const { error } = await supabase
    .from('knowledge_documents')
    .update({ lifecycle_status: lifecycle })
    .eq('id', id);
  if (error) throw error;
}

export async function setKnowledgeQuality(id: string, score: number): Promise<void> {
  const { error } = await supabase
    .from('knowledge_documents')
    .update({ quality_score: Math.max(1, Math.min(5, Math.round(score))) })
    .eq('id', id);
  if (error) throw error;
}

export async function setKnowledgeReviewDate(id: string, reviewAt: string | null): Promise<void> {
  const { error } = await supabase
    .from('knowledge_documents')
    .update({ review_at: reviewAt })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteKnowledgeDocument(id: string): Promise<void> {
  // Chunks cascade with the document, so retrieval loses it immediately.
  const { error } = await supabase.from('knowledge_documents').delete().eq('id', id);
  if (error) throw error;
}

export async function getKnowledgeDocumentContent(id: string): Promise<string> {
  const { data, error } = await supabase
    .from('knowledge_documents')
    .select('content')
    .eq('id', id)
    .single();
  if (error) throw error;
  return (data?.content as string) ?? '';
}

// --- House methodology seeding (platform admin only) ------------------------
// Seeded documents are tagged `seed`, arrive as draft and are removable in one
// action, so nothing enters retrieval without a human approving it.

export type HouseSeedStatus = {
  available: number;
  seeded: number;
  documents: Array<{ id: string; title: string; lifecycle_status: string; chunk_count: number }>;
};

async function callSeedFn<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('seed-house-knowledge', { body });
  if (error) throw error;
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
  return data as T;
}

export function getHouseSeedStatus(): Promise<HouseSeedStatus> {
  return callSeedFn<HouseSeedStatus>({ action: 'status' });
}

export function seedHouseMethodology(approve = true): Promise<{ seeded: number; note: string }> {
  return callSeedFn({ action: 'seed', approve });
}

export function removeHouseMethodologySeed(): Promise<{ removed: number }> {
  return callSeedFn({ action: 'remove' });
}
